import { motion } from "framer-motion";
import { Target, Compass } from "lucide-react";

const VISION = [
  "To become a trusted leader in financial advisory and private capital.",
  "To empower businesses with accessible and growth-driven funding solutions.",
  "To build long-term partnerships based on trust and transparency.",
];

const MISSION = [
  "Deliver tailored funding solutions aligned with business goals.",
  "Ensure seamless access to capital with speed and efficiency.",
  "Maintain the highest standards of integrity and confidentiality.",
  "Act as a strategic partner for sustainable business growth.",
];

const PARAGRAPHS = [
  "Catalyst Global Partners provides comprehensive financial solutions for large, medium, small, and emerging corporates, as well as their wider business ecosystems. We offer a diverse range of products and services tailored to each client's unique business requirements, industry dynamics, market conditions, and growth stage. Our mission is to help businesses plan strategically, manage efficiently, and achieve sustainable growth.",
  "In today's rapidly evolving business environment, access to the right capital at the right time is critical. We partner with organizations across sectors to design financing solutions that support expansion, strengthen operations, optimize capital structures, and unlock new opportunities. Whether you are a growing enterprise seeking working capital, an established company pursuing strategic acquisitions, or an emerging business preparing for its next phase of growth, our team is committed to delivering solutions aligned with your objectives.",
  "At Catalyst Global Partners, our experienced professionals work closely with management teams, promoters, investors, and stakeholders to gain a deep understanding of each business's operational and financial needs. This collaborative approach enables us to structure customized financing solutions that maximize value while balancing risk, flexibility, and long-term sustainability.",
  "Our strength lies in combining deep financial expertise with industry knowledge and technology-driven insights. By continuously monitoring market developments, regulatory changes, and emerging opportunities, we help our clients navigate complex financial landscapes with confidence. Our advisory-driven model ensures that every recommendation is practical, strategic, and aligned with the client's broader business goals.",
  "We are committed to building enduring relationships founded on integrity, transparency, and trust. Rather than offering one-size-fits-all solutions, we focus on understanding the unique challenges and ambitions of every client — providing objective advice and personalized support throughout the funding journey.",
  "At Catalyst Global Partners, we believe that successful financing is about more than capital — it is about creating the foundation for long-term growth, resilience, and value creation. Our commitment is simple: to act in our clients' best interests, deliver exceptional service, and provide innovative financial solutions that empower businesses to thrive in an increasingly competitive world.",
];

export default function CapitalFundingAbout() {
  return (
    <section
      id="about-capital-funding"
      data-testid="capital-funding-about-section"
      className="bg-[#FAFAF7] py-24 md:py-32"
    >
      <div className="cgp-container">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <span className="cgp-overline">About Our Company</span>
          <h2 className="font-serif font-light text-[#0A1F44] mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Your trusted partner for
            <br />
            <span className="italic text-[#0A1F44]/70">
              growth, capital &amp; strategy.
            </span>
          </h2>
          <p className="mt-7 font-serif italic text-[#0A1F44]/80 text-xl md:text-2xl leading-snug max-w-2xl">
            Catalyst Global Partners — your trusted partner for growth, capital,
            and strategic financial solutions.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16"
        >
          <div className="lg:col-span-7 space-y-5">
            {PARAGRAPHS.map((p, i) => (
              <p
                key={i}
                className="text-[#0B1A36]/80 text-base md:text-[1.05rem] leading-relaxed"
              >
                {p}
              </p>
            ))}
          </div>

          {/* Vision & Mission cards */}
          <div className="lg:col-span-5 space-y-6">
            <div
              data-testid="vision-card"
              className="bg-white border border-[#0A1F44]/10 p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#C8A24A]/12 border border-[#C8A24A]/30 text-[#C8A24A]">
                  <Target size={16} strokeWidth={1.8} />
                </span>
                <h3 className="font-serif text-xl text-[#0A1F44]">Vision</h3>
              </div>
              <ul className="space-y-3">
                {VISION.map((v) => (
                  <li
                    key={v}
                    className="flex items-start gap-3 text-[#0B1A36]/75 text-sm leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A] mt-2 shrink-0" />
                    <span>{v}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="mission-card"
              className="bg-[#0A1F44] text-white p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#C8A24A]/15 border border-[#C8A24A]/40 text-[#C8A24A]">
                  <Compass size={16} strokeWidth={1.8} />
                </span>
                <h3 className="font-serif text-xl text-white">Mission</h3>
              </div>
              <ul className="space-y-3">
                {MISSION.map((m) => (
                  <li
                    key={m}
                    className="flex items-start gap-3 text-white/75 text-sm leading-relaxed"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A24A] mt-2 shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
